import { AnyAction, ListenerEffectAPI, ListenerMiddlewareInstance, ThunkDispatch } from '@reduxjs/toolkit'
import { AppState } from '@redux/store'
import { CartBundle } from '@typesApp/Product'
import { ActionModule } from '@typesApp/ActionModule'
import { UpdateProductArgs } from '@typesApp/CustomerApi'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { setProductUpdateInProgress } from '@redux/slices/connectorSlice'
import { unwrapMutationActionResult } from '@redux/queries/unwrap'
import { shouldRefreshCartTotals } from '@utils/claimUtil'
import { isProductUpdateRequired } from './cartProductUtil'
import {
  CartProductUpdateController,
  cartProductUpdateController,
  CartProductUpdateService,
  CartProductUpdateParams,
} from './cartProductUpdateController'

type ListenerApi = ListenerEffectAPI<unknown, ThunkDispatch<unknown, unknown, AnyAction>, unknown>
type ListenerInstance = ListenerMiddlewareInstance<unknown, ThunkDispatch<unknown, unknown, AnyAction>, unknown>

/**
 * Update products if needed on claim record fetch
 */
export const initCartProductUpdateListener = (listenerMiddleware: ListenerInstance) => {
  listenerMiddleware.startListening({
    matcher: claimRecordsApi.endpoints.getClaimRecord.matchFulfilled,

    effect: async (action, listenerApi) => {
      const state = listenerApi.getState() as AppState
      const claimRecord = action.payload
      const isInProgress = state.connector.isProductUpdateInProgress

      if (!claimRecord || isInProgress) return
      if (!isProductUpdateRequired(claimRecord?.cartStatus) && !shouldRefreshCartTotals(claimRecord)) return

      listenerApi.dispatch(setProductUpdateInProgress(true))

      try {
        const controller = getController(listenerApi)
        await controller.performUpdateProducts(claimRecord)
      } catch (error) {
        console.warn('directbilling - update product error', error)
      } finally {
        listenerApi.dispatch(setProductUpdateInProgress(false))
      }
    },
  })
}

const getController = (listenerApi: ListenerApi): CartProductUpdateController => {
  const params = getControllerParams(listenerApi)
  const service = getControllerService(listenerApi)
  return cartProductUpdateController(service, params)
}

const getControllerParams = (listenerApi: ListenerApi): CartProductUpdateParams => {
  const { ecommerceCode, ecommerceTransactionHost } = getCommerceCodeParams(listenerApi)
  const customerNumber = getCustomerNumber(listenerApi)

  return {
    customerNumber,
    ecommerceCode,
    ecommerceTransactionHost,
  }
}

const getControllerService = (listenerApi: ListenerApi): CartProductUpdateService => {
  const updateProductsMutation = async (args: UpdateProductArgs) => {
    return unwrapMutationActionResult<void>(
      listenerApi.dispatch(claimRecordsApi.endpoints.updateProducts.initiate(args))
    )
  }

  const updateRefreshCartFlagMutation = async (claimRecordId: string) => {
    return unwrapMutationActionResult<void>(
      listenerApi.dispatch(claimRecordsApi.endpoints.updateRefreshCartTotalFlag.initiate(claimRecordId))
    )
  }

  const fetchCartBundle = (): Promise<CartBundle> => getActionModule(listenerApi).getCartItemDetails()

  const refreshCart = (): Promise<void> => getActionModule(listenerApi).refreshCart()

  return {
    updateProductsMutation,
    updateRefreshCartFlagMutation,
    fetchCartBundle,
    refreshCart,
  }
}

const getCustomerNumber = (listenerApi: ListenerApi): string | undefined => {
  const state = listenerApi.getState() as AppState
  return state.connector.customer?.id
}

const getActionModule = (listenerApi: ListenerApi): ActionModule => {
  const state = listenerApi.getState() as AppState
  const actionModule = state.connector.actionModule
  if (!actionModule) {
    throw new Error('Bad configuration, action module missing!')
  }
  return actionModule
}

const getCommerceCodeParams = (
  listenerApi: ListenerApi
): { ecommerceCode: string; ecommerceTransactionHost: string | undefined } => {
  const state = listenerApi.getState() as AppState
  const { ecommerceCode, ecommerceTransactionHost } = state.connector?.environment ?? {}
  return {
    ecommerceCode,
    ecommerceTransactionHost,
  }
}
